import React, { useState, useEffect } from "react";
import { Box, Typography, Link, Divider } from "@mui/material";
import { Business as BusinessIcon, Person as PersonIcon } from "@mui/icons-material";
import { getAdminCompanyId, adminLogout } from "../../api/api";
import Cookies from "js-cookie";
import Swal from 'sweetalert2';
import { navLinkStyles } from "../../theme/theme";

const navLinkSx = {
    fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.95rem' },
    fontWeight: 600,
    color: '#ffffff',
    textDecoration: 'none',
    px: { xs: 0.5, sm: 0.8 },
    py: { xs: 0.3, sm: 0.2 },
    borderRadius: 1,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.4,
    minWidth: { xs: 'auto', sm: 'fit-content' },
    textAlign: 'center',
};

const NavLinkContent = ({ icon: Icon, label }) => (
    <>
        {Icon && <Icon sx={{ fontSize: '1.1em', color: 'inherit' }} />}
        {label}
    </>
);

const AdminHeader = ({ 
    title, 
    icon: Icon, 
    navigationLinks = [],
    sx = {} 
}) => {
    const [companyName, setCompanyName] = useState("");
    const [adminName, setAdminName] = useState("");

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const admin_id = Cookies.get("admin_id");
                if (admin_id) {
                    const response = await getAdminCompanyId(admin_id);
                    setCompanyName(response.data.company.name);
                    setAdminName(response.data.admin.name);
                }
            } catch (error) {
                console.error("Error fetching company:", error);
            }
        };
        
        fetchCompany();
    }, []);

    const handleLogout = async (e) => {
        e.preventDefault();
       // console.log('Logout clicked'); // Debug log
        
        try {
            const result = await Swal.fire({
                title: 'Confirm Logout',
                text: "Are you sure you want to logout from the admin panel?",
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, Logout',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
                focusCancel: true // Focus on Cancel button by default
            });

          //  console.log('SweetAlert result:', result); // Debug log

            if (result.isConfirmed) {
              //  console.log('User confirmed logout'); // Debug log
                
                try {
                    // Call backend logout API to log the logout
                    await adminLogout();
                } catch (error) {
                    console.error('Error calling logout API:', error);
                    // Continue with logout even if API call fails
                }
                
                // Clear cookies
                Cookies.remove('admin_id');
                Cookies.remove('admin_company_id');
                
                // Show logout success message
                Swal.fire({
                    title: 'Logged Out Successfully!',
                    text: 'You have been logged out of the admin panel.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then(() => {
                    // Redirect to login page
                    window.location.href = '/login';
                });
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                console.log('User cancelled logout'); // Debug log
                // User clicked Cancel - do nothing, stay on current page
                return;
            }
        } catch (error) {
            console.error('Error in logout confirmation:', error);
            // If there's an error with the confirmation dialog, show error message
            Swal.fire({
                title: 'Error',
                text: 'An error occurred. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    return (
        <Box sx={{ 
            mb: 1, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            p: { xs: 1.5, sm: 2 },
            bgcolor: 'primary.main',
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: '0 4px 20px rgba(25, 118, 210, 0.25)',
            color: 'white',
            gap: { xs: 2, md: 0 },
            ...sx
        }}>
            {/* Left Side - Company and Admin Info */}
            <Box sx={{ 
                display: 'flex', 
                gap: { xs: 2, sm: 3 }, 
                flexWrap: 'wrap', 
                alignItems: 'center',
                justifyContent: { xs: 'center', md: 'flex-start' },
                order: { xs: 2, md: 1 }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600,
                            color: 'white',
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                            textAlign: { xs: 'center', md: 'left' }
                        }}
                    >
                        {companyName || "Company Dashboard"}
                    </Typography>
                </Box>
                <Divider 
                    orientation="vertical" 
                    flexItem 
                    sx={{ 
                        bgcolor: 'rgba(255,255,255,0.3)', 
                        height: { xs: 20, sm: 30 },
                        display: { xs: 'none', sm: 'block' }
                    }} 
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            fontWeight: 500,
                            color: 'white',
                            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                            textAlign: { xs: 'center', md: 'left' }
                        }}
                    >
                        {adminName || "Admin Panel"}
                    </Typography>
                </Box>
            </Box>
            
            {/* Center - Main Title */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, sm: 2 }, 
                flex: { xs: 'none', md: 1 }, 
                justifyContent: 'center',
                order: { xs: 1, md: 2 }
            }}>
                {Icon && <Icon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: 'white' }} />}
                <Typography 
                    variant="h6" 
                    sx={{ 
                        fontWeight: 700, 
                        color: 'white',
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                        textAlign: 'center'
                    }}
                >
                    {title}
                </Typography>
            </Box>
            
            {/* Right Side - Navigation Links */}
            {navigationLinks.length > 0 && (
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 0.3, sm: 0.5 },
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    order: { xs: 3, md: 3 },
                    mt: { xs: 1, md: 0 },
                    flexShrink: 0,
                    /* Desktop / tablet landscape: compact row, width follows content (previous behavior) */
                    width: 'auto',
                    maxWidth: 'none',
                    overflowX: 'visible',
                    /* Small screens: full-width single row + horizontal scroll */
                    '@media (max-width: 899.95px)': {
                        flexWrap: 'nowrap',
                        justifyContent: 'flex-start',
                        width: '100%',
                        maxWidth: '100%',
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        pb: 0.25,
                        alignItems: 'stretch',
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'rgba(255,255,255,0.35)',
                            borderRadius: 3,
                        },
                    },
                }}>
                    {navigationLinks.map((link, index) => {
                        const isLogoutLink = link.href === '/admin_logout' || link.label.includes('Logout');
                        const linkStyles = link.variant === 'danger' ? navLinkStyles.danger : navLinkStyles.default;
                        
                        if (isLogoutLink) {
                            return (
                                <Box
                                    key={index}
                                    component="span"
                                    onClick={handleLogout}
                                    sx={{
                                        ...navLinkSx,
                                        background: link.background || linkStyles.background,
                                        '&:hover': {
                                            background: link.hoverBackground || linkStyles.hoverBackground,
                                            textDecoration: 'none',
                                            color: '#ffffff',
                                        },
                                    }}
                                >
                                    <NavLinkContent icon={link.icon} label={link.label} />
                                </Box>
                            );
                        }
                        
                        return (
                            <Link 
                                key={index}
                                href={link.href}
                                sx={{
                                    ...navLinkSx,
                                    background: link.background || linkStyles.background,
                                    '&:hover': {
                                        background: link.hoverBackground || linkStyles.hoverBackground,
                                        textDecoration: 'none',
                                        color: '#ffffff',
                                    },
                                }}
                            >
                                <NavLinkContent icon={link.icon} label={link.label} />
                            </Link>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default AdminHeader;
